<div class="container-fluid complyon-page governance-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell governance-shell mx-auto">
    <div class="mb-3">
      <h1 class="complyon-title governance-title mb-2 text-l">Company governance</h1>
      <p class="complyon-subtitle governance-subtitle mb-0 text-s">
        Board decisions, statutory records, resolutions, committees and governance evidence.
      </p>
    </div>

    <div class="d-flex flex-column flex-xl-row align-items-start justify-content-between gap-3 mb-3 governance-toolbar">
      <div class="d-flex flex-wrap align-items-center gap-2 governance-tab-row" role="tablist" aria-label="Company governance tabs">
        <button
          v-for="tab in governance_tabs"
          :key="tab.id"
          type="button"
          class="btn governance-tab-btn d-inline-flex align-items-center gap-2"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="governance-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <button type="button" class="btn btn-link text-decoration-none governance-reset-btn d-inline-flex align-items-center gap-2">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </div>

    <div v-if="active_tab === 'overview'">
      <div class="row g-3 mb-3">
        <div
          v-for="card in summary_cards"
          :key="card.title"
          class="col-12 col-md-6 col-xl-3"
        >
          <div
            class="complyon-card governance-card rounded-4 p-3 h-100"
            :class="{ 'governance-card--clickable': !!card.tab_id }"
            :role="card.tab_id ? 'button' : null"
            :tabindex="card.tab_id ? 0 : -1"
            @click="card.tab_id && set_tab(card.tab_id)"
            @keydown.enter.prevent="card.tab_id && set_tab(card.tab_id)"
            @keydown.space.prevent="card.tab_id && set_tab(card.tab_id)"
          >
            <div class="governance-stat-label text-xs">{{ card.title }}</div>
            <div class="governance-stat-value" :class="`is-${card.tone}`">{{ card.value }}</div>
            <div class="governance-stat-note text-xs">{{ card.note }}</div>
          </div>
        </div>
      </div>

      <div class="complyon-panel governance-bridge-card rounded-4 overflow-hidden mb-3">
        <div class="px-3 px-md-4 pt-3 pt-md-4">
          <div class="governance-section-title text-s">The structure of accountability - connects to</div>
          <div class="governance-section-subtitle text-xs">Governance is the joining module that turns board and committee decisions into enforceable action across the platform.</div>
        </div>
          <div class="px-3 px-md-4 pb-3 pb-md-4 pt-3">
            <div class="d-flex flex-wrap gap-2">
              <router-link v-for="chip in bridge_chips" :key="chip.label" :to="{ name: chip.route_name }" class="badge rounded-pill governance-chip d-inline-flex align-items-center gap-2 text-decoration-none">
                <i class="bi" :class="chip.icon"></i>
                <span>{{ chip.label }}</span>
              </router-link>
            </div>
          </div>
        </div>

      <div class="complyon-panel governance-panel rounded-4 overflow-hidden mb-3">
        <div class="px-3 px-md-4 pt-3 pt-md-4">
          <div class="governance-section-title text-s">Governance framework</div>
          <div class="governance-section-subtitle text-xs">How the organisation is directed, controlled and held accountable.</div>
        </div>
        <div class="px-3 px-md-4 py-3 py-md-4">
          <div class="row g-3">
            <div
              v-for="card in framework_cards"
              :key="card.title"
              class="col-12 col-xl-4"
            >
              <div class="governance-framework-card rounded-4 p-3 h-100">
                <div :class="['governance-framework-icon d-inline-flex align-items-center justify-content-center', `is-${card.icon_tone}`]">
                  <i class="bi" :class="card.icon"></i>
                </div>
                <div class="governance-framework-title mt-3">{{ card.title }}</div>
                <div class="governance-framework-copy">{{ card.copy }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-3">
        <div class="col-12 col-xl-7">
          <div class="complyon-panel governance-panel rounded-4 overflow-hidden h-100">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
              <div>
                <div class="governance-section-title text-s">Governance maturity score</div>
                <div class="governance-section-subtitle text-xs">How mature each area is - adjust as evidence improves. Click - / + to update.</div>
              </div>
            </div>

            <div class="px-3 px-md-4 py-3 py-md-4">
              <div
                v-for="row in maturity_rows"
                :key="row.label"
                class="governance-maturity-row"
              >
                <div class="governance-maturity-line d-flex align-items-center gap-3">
                  <div class="governance-maturity-label">{{ row.label }}</div>
                  <div class="governance-maturity-bars d-flex gap-1 flex-grow-1">
                    <span v-for="n in row.max" :key="`${row.label}-${n}`" :class="{ active: n <= row.value }"></span>
                  </div>
                  <div class="governance-maturity-meta">{{ row.value }}/{{ row.max }} - {{ row.status }}</div>
                  <button type="button" class="governance-maturity-stepper" aria-label="Decrease maturity" @click="adjust_maturity(row, -1)">-</button>
                  <button type="button" class="governance-maturity-stepper" aria-label="Increase maturity" @click="adjust_maturity(row, 1)">+</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-5">
          <div class="complyon-panel governance-panel rounded-4 overflow-hidden h-100">
            <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
              <div>
                <div class="governance-section-title text-s">Committee structure</div>
                <div class="governance-section-subtitle text-xs">Bodies that govern compliance</div>
              </div>
              <button type="button" class="btn btn-outline-secondary governance-view-btn" @click="set_tab('committees')">View all</button>
            </div>

            <div class="px-3 px-md-4 py-3 py-md-4">
              <div class="governance-committee-list">
                <div
                  v-for="item in committee_items"
                  :key="item.title"
                  class="governance-committee-row d-flex align-items-center justify-content-between gap-3"
                  role="button"
                  tabindex="0"
                  @click="set_tab(item.tab_id)"
                  @keydown.enter.prevent="set_tab(item.tab_id)"
                  @keydown.space.prevent="set_tab(item.tab_id)"
                >
                  <div class="d-flex align-items-start gap-3 min-w-0">
                    <span :class="['governance-committee-icon d-inline-flex align-items-center justify-content-center flex-shrink-0', `is-${item.icon_tone}`]">
                      <i class="bi" :class="item.icon"></i>
                    </span>
                    <div class="min-w-0">
                      <div class="governance-committee-title">{{ item.title }}</div>
                      <div class="governance-committee-copy">{{ item.subtitle }}</div>
                    </div>
                  </div>
                  <span class="badge rounded-pill governance-pill text-xs" :class="item.status_class">{{ item.status }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'committees'">
      <div class="complyon-panel governance-panel rounded-4 overflow-hidden">
        <div class="d-flex flex-column flex-md-row align-items-md-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="governance-section-title text-s">Committee structures &amp; terms of reference</div>
            <div class="governance-section-subtitle text-xs">Each governing body, its chair, cadence and mandate. Click a card to edit its terms of reference.</div>
          </div>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="open_committee_modal()">
            <i class="bi bi-plus"></i>
            <span>Add committee</span>
          </button>
        </div>
        <div class="px-3 px-md-4 pb-3 pb-md-4">
          <div class="row g-3">
            <div v-for="(card, index) in committee_cards" :key="card.id" class="col-12 col-xl-6">
              <div class="governance-committee-card rounded-4 p-3 h-100 governance-committee-card--clickable" role="button" tabindex="0" @click="open_committee_modal(card, index)" @keydown.enter.prevent="open_committee_modal(card, index)" @keydown.space.prevent="open_committee_modal(card, index)">
                <div class="d-flex align-items-start justify-content-between gap-3">
                  <div class="d-flex align-items-start gap-3 min-w-0">
                    <span :class="['governance-framework-icon governance-committee-card-icon d-inline-flex align-items-center justify-content-center flex-shrink-0', `is-${card.icon_tone}`]">
                      <i class="bi" :class="card.icon"></i>
                    </span>
                    <div class="min-w-0">
                      <div class="governance-committee-card-title">{{ card.title }}</div>
                      <div class="governance-committee-card-label">{{ card.label }}</div>
                    </div>
                  </div>
                  <span class="badge rounded-pill governance-pill text-xs" :class="card.status_class">{{ card.status }}</span>
                </div>
                <div class="governance-committee-card-copy mt-3">{{ card.copy }}</div>
                <div class="governance-committee-card-footer mt-3 pt-2">
                  <div v-for="meta in card.footer" :key="`${card.title}-${meta.text}`" class="governance-committee-card-meta">
                    <i class="bi" :class="meta.icon"></i>
                    <span>{{ meta.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'decisions'">
      <div class="complyon-panel governance-panel rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="governance-section-title text-s">Decision &amp; resolution register</div>
            <div class="governance-section-subtitle text-xs">Every major decision - who approved it, under what authority, what it authorises, and the enforcement it triggers. Click a row for the full chain.</div>
          </div>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="open_governance_modal('decision')">
            <i class="bi bi-plus"></i>
            <span>Add</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table governance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Ref</th>
                <th class="text-xs">Decision</th>
                <th class="text-xs">Approved by</th>
                <th class="text-xs">Linked risk</th>
                <th class="text-xs">Authorises</th>
                <th class="text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in decision_rows"
                :key="row.id || row.ref"
                :class="{ 'governance-row-highlight': row.highlight }"
                class="governance-row-clickable"
                role="button"
                tabindex="0"
                :title="`Open ${row.decision}`"
                @click="open_decision_drawer(row, index)"
                @keydown.enter.prevent="open_decision_drawer(row, index)"
                @keydown.space.prevent="open_decision_drawer(row, index)"
              >
                <td class="ps-3 fw-semibold text-nowrap">{{ row.ref }}</td>
                <td>
                  <div class="governance-item">{{ row.decision }}</div>
                  <div v-if="row.note" class="governance-subtext">{{ row.note }}</div>
                </td>
                <td>{{ row.approved_by }}</td>
                <td>{{ row.linked_risk }}</td>
                <td class="governance-link-cell">{{ row.authorises }}</td>
                <td><span class="badge rounded-pill governance-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="complyon-panel governance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="governance-section-title text-s">Meeting action items</div>
            <div class="governance-section-subtitle text-xs">Actions arising from committee meetings, with owner and due date.</div>
          </div>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="open_governance_modal('action')">
            <i class="bi bi-plus"></i>
            <span>Add</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table governance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Action</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Raised in</th>
                <th class="text-xs">Due</th>
                <th class="text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in action_rows"
                :key="row.id || row.action"
                class="governance-row-clickable"
                role="button"
                tabindex="0"
                :title="`Open ${row.action}`"
                @click="open_governance_modal('action', row, index)"
                @keydown.enter.prevent="open_governance_modal('action', row, index)"
                @keydown.space.prevent="open_governance_modal('action', row, index)"
              >
                <td class="ps-3 fw-semibold">{{ row.action }}</td>
                <td>{{ row.owner }}</td>
                <td>{{ row.raised_in }}</td>
                <td>{{ row.due }}</td>
                <td><span class="badge rounded-pill governance-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'accountability'">
      <div class="complyon-panel governance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="governance-section-title text-s">Accountability map</div>
            <div class="governance-section-subtitle text-xs">Who is accountable for each compliance area, who executes it, which committee oversees it - and where the proof lives.</div>
          </div>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="open_governance_modal('accountability')">
            <i class="bi bi-plus"></i>
            <span>Add area</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table governance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Compliance area</th>
                <th class="text-xs">Accountable</th>
                <th class="text-xs">Responsible</th>
                <th class="text-xs">Oversight</th>
                <th class="text-xs">Proof</th>
                <th class="text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in accountability_rows"
                :key="row.id || row.area"
                class="governance-row-clickable"
                role="button"
                tabindex="0"
                :title="`Open ${row.area}`"
                @click="open_governance_modal('accountability', row, index)"
                @keydown.enter.prevent="open_governance_modal('accountability', row, index)"
                @keydown.space.prevent="open_governance_modal('accountability', row, index)"
              >
                <td class="ps-3 fw-semibold">{{ row.area }}</td>
                <td>{{ row.accountable }}</td>
                <td>{{ row.responsible }}</td>
                <td>{{ row.oversight }}</td>
                <td class="governance-link-cell">{{ row.proof }}</td>
                <td><span class="badge rounded-pill governance-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'calendar'">
      <div class="complyon-panel governance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="governance-section-title text-s">Governance calendar</div>
            <div class="governance-section-subtitle text-xs">Board and committee meetings, reporting deadlines and policy review dates.</div>
          </div>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="open_governance_modal('calendar')">
            <i class="bi bi-plus"></i>
            <span>Add event</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table governance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Event</th>
                <th class="text-xs">Type</th>
                <th class="text-xs">Date</th>
                <th class="text-xs">Owner</th>
                <th class="text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in calendar_rows"
                :key="row.id || row.event"
                class="governance-row-clickable"
                role="button"
                tabindex="0"
                :title="`Open ${row.event}`"
                @click="open_governance_modal('calendar', row, index)"
                @keydown.enter.prevent="open_governance_modal('calendar', row, index)"
                @keydown.space.prevent="open_governance_modal('calendar', row, index)"
              >
                <td class="ps-3 fw-semibold">
                  <span class="governance-table-icon"><i class="bi" :class="row.icon"></i></span>
                  <span>{{ row.event }}</span>
                </td>
                <td>{{ row.type }}</td>
                <td>{{ row.date }}</td>
                <td>{{ row.owner }}</td>
                <td><span class="badge rounded-pill governance-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'consequences'">
      <div class="complyon-panel governance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
          <div>
            <div class="governance-section-title text-s">Consequence management tracker</div>
            <div class="governance-section-subtitle text-xs">Breaches and how they were handled - responsible party, action taken, and the corrective or disciplinary step.</div>
          </div>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="open_governance_modal('consequence')">
            <i class="bi bi-plus"></i>
            <span>Log a case</span>
          </button>
        </div>
        <div class="table-responsive">
          <table class="table complyon-table governance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3 text-xs">Breach / incident</th>
                <th class="text-xs">Responsible party</th>
                <th class="text-xs">Severity</th>
                <th class="text-xs">Action taken</th>
                <th class="text-xs">Corrective step</th>
                <th class="text-xs">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, index) in consequence_rows"
                :key="row.id || row.breach"
                class="governance-row-clickable"
                role="button"
                tabindex="0"
                :title="`Open ${row.breach}`"
                @click="open_governance_modal('consequence', row, index)"
                @keydown.enter.prevent="open_governance_modal('consequence', row, index)"
                @keydown.space.prevent="open_governance_modal('consequence', row, index)"
              >
                <td class="ps-3 fw-semibold">{{ row.breach }}</td>
                <td>{{ row.responsible_party }}</td>
                <td>{{ row.severity }}</td>
                <td>{{ row.action_taken }}</td>
                <td class="governance-link-cell">{{ row.corrective_step }}</td>
                <td><span class="badge rounded-pill governance-pill text-xs" :class="row.status_class">{{ row.status }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="committee_modal_open" class="governance-modal-backdrop" @click="close_committee_modal"></div>

    <section
      v-if="committee_modal_open"
      class="governance-modal-shell"
      role="dialog"
      aria-modal="true"
      :aria-label="committee_modal_mode === 'edit' ? 'Edit committee' : 'New committee'"
      @click.stop
    >
      <div class="governance-modal-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <h2 class="governance-modal-title mb-1 text-truncate">{{ committee_modal_mode === 'edit' ? 'Edit committee' : 'New committee' }}</h2>
            <div class="governance-modal-subtitle text-truncate">Define the body, its mandate and how it meets.</div>
          </div>
          <button type="button" class="btn btn-link text-decoration-none p-0 governance-modal-close" @click="close_committee_modal" aria-label="Close committee dialog">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>

      <div class="governance-modal-body">
        <div class="row g-3">
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Committee name</label>
            <input v-model="committee_modal_form.name" type="text" class="form-control governance-form-control" placeholder="e.g. Social &amp; Ethics Committee" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Type</label>
            <select v-model="committee_modal_form.type" class="form-select governance-form-control">
              <option v-for="option in committee_type_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Chairperson</label>
            <input v-model="committee_modal_form.chairperson" type="text" class="form-control governance-form-control" placeholder="Name (role)" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Members</label>
            <input v-model="committee_modal_form.members" type="text" class="form-control governance-form-control" placeholder="e.g. 5 members" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Meeting cadence</label>
            <select v-model="committee_modal_form.meeting_cadence" class="form-select governance-form-control">
              <option v-for="option in committee_cadence_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Quorum</label>
            <input v-model="committee_modal_form.quorum" type="text" class="form-control governance-form-control" placeholder="e.g. 3 of 5" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Status</label>
            <select v-model="committee_modal_form.status" class="form-select governance-form-control">
              <option v-for="option in committee_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>

        <div class="mt-3">
          <label class="governance-field-label text-xs">Terms of reference (mandate)</label>
          <textarea
            v-model="committee_modal_form.mandate"
            class="form-control governance-form-control governance-form-textarea"
            rows="5"
            placeholder="What this committee is mandated to direct, control and decide."
          ></textarea>
        </div>
      </div>

      <div class="governance-modal-footer border-top d-flex align-items-center justify-content-end gap-2">
        <button
          v-if="committee_modal_mode === 'edit'"
          type="button"
          class="btn btn-link text-decoration-none me-auto text-danger"
          @click="delete_committee_modal"
        >
          <i class="bi bi-trash3 me-1"></i>
          <span>Delete</span>
        </button>
        <button type="button" class="btn btn-link text-decoration-none" @click="close_committee_modal">Cancel</button>
        <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="save_committee_modal">
          <i class="bi" :class="committee_modal_mode === 'edit' ? 'bi-check2-square' : 'bi-plus-lg'"></i>
          <span>{{ committee_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
        </button>
      </div>
    </section>

    <div v-if="active_decision_drawer" class="command-centre-drawer-backdrop" @click="close_decision_drawer"></div>

    <aside
      v-if="active_decision_drawer"
      class="command-centre-drawer command-centre-drawer--governance"
      aria-label="Decision drawer"
      @click.stop
    >
      <div class="command-centre-drawer-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="governance-drawer-kicker text-uppercase">{{ active_decision_drawer.section }}</div>
            <h2 class="governance-drawer-title mb-1 text-truncate">{{ active_decision_drawer.title }}</h2>
            <div class="governance-drawer-subtitle text-truncate">{{ active_decision_drawer.subtitle }}</div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <span class="badge rounded-pill" :class="active_decision_drawer.status_class">{{ active_decision_drawer.status }}</span>
            <button type="button" class="btn btn-link text-decoration-none p-0 governance-modal-close" @click="close_decision_drawer" aria-label="Close drawer">
              <i class="bi bi-x-lg fs-5"></i>
            </button>
          </div>
        </div>
      </div>

      <div ref="decisionDrawerBody" class="command-centre-drawer-body">
        <div class="governance-drawer-summary-grid mb-4">
          <div v-for="card in active_decision_drawer.summary_cards" :key="card.label" class="governance-drawer-summary-card rounded-4 p-3">
            <div class="governance-drawer-summary-label text-uppercase">{{ card.label }}</div>
            <div class="governance-drawer-summary-value" :class="card.value_class || ''">{{ card.value }}</div>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <div class="governance-drawer-section-title">Connected records - what this resolution joins</div>
        </div>
        <div class="governance-drawer-record-grid mb-4">
          <div v-for="record in active_decision_drawer.connected_records" :key="record.label" class="governance-drawer-record-card rounded-4 p-3">
            <div class="d-flex align-items-start gap-2">
              <span class="governance-drawer-record-icon d-inline-flex align-items-center justify-content-center">
                <i class="bi" :class="record.icon"></i>
              </span>
              <div class="min-w-0">
                <div class="governance-drawer-record-label text-uppercase">{{ record.label }}</div>
                <div class="governance-drawer-record-value text-truncate">{{ record.value }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
          <div class="governance-drawer-section-title">Enforcement chain - how this decision becomes action</div>
          <span class="badge rounded-pill" :class="active_decision_drawer.status_class">{{ active_decision_drawer.status }}</span>
        </div>
        <div class="governance-drawer-chain">
          <div v-for="(step, step_index) in active_decision_drawer.enforcement_chain" :key="`${active_decision_drawer.section}-${step_index}`" class="governance-drawer-chain-item rounded-4 p-3">
            <div class="d-flex align-items-start gap-3">
              <span class="governance-drawer-chain-icon d-inline-flex align-items-center justify-content-center">
                <i class="bi bi-arrow-right-short"></i>
              </span>
              <div class="min-w-0">
                <div class="governance-drawer-chain-title">{{ step.title }}</div>
                <div class="governance-drawer-chain-subtitle">{{ step.subtitle }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="command-centre-drawer-footer border-top">
        <div class="d-flex align-items-center justify-content-between gap-3">
          <button type="button" class="btn btn-link text-decoration-none px-0" @click="close_decision_drawer">Close</button>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="edit_active_decision">
            <i class="bi bi-pencil-square"></i>
            <span>Edit resolution</span>
          </button>
        </div>
      </div>
    </aside>

    <div v-if="governance_modal_open" class="governance-modal-backdrop" @click="close_governance_modal"></div>

    <section
      v-if="governance_modal_open"
      class="governance-modal-shell"
      role="dialog"
      aria-modal="true"
      :aria-label="governance_modal_kind === 'action'
        ? (governance_modal_mode === 'edit' ? 'Edit action item' : 'New action item')
        : governance_modal_kind === 'accountability'
          ? (governance_modal_mode === 'edit' ? 'Edit accountability mapping' : 'New accountability mapping')
          : governance_modal_kind === 'calendar'
            ? (governance_modal_mode === 'edit' ? 'Edit calendar event' : 'New calendar event')
            : governance_modal_kind === 'consequence'
              ? (governance_modal_mode === 'edit' ? 'Edit consequence case' : 'New consequence case')
              : (governance_modal_mode === 'edit' ? 'Edit decision / resolution' : 'New decision / resolution')"
      @click.stop
    >
      <div class="governance-modal-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <h2 class="governance-modal-title mb-1 text-truncate">
              <span v-if="governance_modal_kind === 'action'">{{ governance_modal_mode === 'edit' ? 'Edit action item' : 'New action item' }}</span>
              <span v-else-if="governance_modal_kind === 'accountability'">{{ governance_modal_mode === 'edit' ? 'Edit accountability mapping' : 'New accountability mapping' }}</span>
              <span v-else-if="governance_modal_kind === 'calendar'">{{ governance_modal_mode === 'edit' ? 'Edit calendar event' : 'New calendar event' }}</span>
              <span v-else-if="governance_modal_kind === 'consequence'">{{ governance_modal_mode === 'edit' ? 'Edit consequence case' : 'New consequence case' }}</span>
              <span v-else>{{ governance_modal_mode === 'edit' ? 'Edit decision / resolution' : 'New decision / resolution' }}</span>
            </h2>
            <div class="governance-modal-subtitle text-truncate">
              <span v-if="governance_modal_kind === 'action'">An action arising from a meeting, with owner and due date.</span>
              <span v-else-if="governance_modal_kind === 'accountability'">Who is accountable for a compliance area, and where the proof lives.</span>
              <span v-else-if="governance_modal_kind === 'calendar'">A meeting, reporting deadline or review date.</span>
              <span v-else-if="governance_modal_kind === 'consequence'">A breach, who was responsible, and the corrective or disciplinary step.</span>
              <span v-else>A formal decision, who approved it, what it authorises and what it enforces.</span>
            </div>
          </div>
          <button type="button" class="btn btn-link text-decoration-none p-0 governance-modal-close" @click="close_governance_modal" aria-label="Close governance dialog">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>

      <div class="governance-modal-body">
        <div v-if="governance_modal_kind === 'action'" class="row g-3">
          <div class="col-12">
            <label class="governance-field-label text-xs">Action</label>
            <input v-model="governance_modal_form.action" type="text" class="form-control governance-form-control" placeholder="e.g. Circulate audit findings" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Owner</label>
            <input v-model="governance_modal_form.owner" type="text" class="form-control governance-form-control" placeholder="Responsible person" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Raised in</label>
            <select v-model="governance_modal_form.raised_in" class="form-select governance-form-control">
              <option v-for="option in committee_type_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Due date</label>
            <input v-model="governance_modal_form.due" type="text" class="form-control governance-form-control" placeholder="e.g. 31 May 2026" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Status</label>
            <select v-model="governance_modal_form.status" class="form-select governance-form-control">
              <option v-for="option in action_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>

        <div v-else-if="governance_modal_kind === 'accountability'" class="row g-3">
          <div class="col-12">
            <label class="governance-field-label text-xs">Compliance area</label>
            <input v-model="governance_modal_form.area" type="text" class="form-control governance-form-control" placeholder="e.g. Anti-bribery & corruption" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Accountable (owns it)</label>
            <input v-model="governance_modal_form.accountable" type="text" class="form-control governance-form-control" placeholder="Role - name" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Responsible (executes)</label>
            <input v-model="governance_modal_form.responsible" type="text" class="form-control governance-form-control" placeholder="Team" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Oversight committee</label>
            <input v-model="governance_modal_form.oversight" type="text" class="form-control governance-form-control" placeholder="e.g. Risk committee" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Where is the proof?</label>
            <input v-model="governance_modal_form.proof" type="text" class="form-control governance-form-control" placeholder="e.g. Risk register, evidence vault" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Status</label>
            <select v-model="governance_modal_form.status" class="form-select governance-form-control">
              <option v-for="option in accountability_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>

        <div v-else-if="governance_modal_kind === 'calendar'" class="row g-3">
          <div class="col-12">
            <label class="governance-field-label text-xs">Event</label>
            <input v-model="governance_modal_form.event" type="text" class="form-control governance-form-control" placeholder="e.g. Board meeting - Q3" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Type</label>
            <select v-model="governance_modal_form.type" class="form-select governance-form-control">
              <option v-for="option in calendar_type_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Date</label>
            <input v-model="governance_modal_form.date" type="text" class="form-control governance-form-control" placeholder="e.g. 12 Jun 2026" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Owner</label>
            <input v-model="governance_modal_form.owner" type="text" class="form-control governance-form-control" placeholder="Responsible person" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Status</label>
            <select v-model="governance_modal_form.status" class="form-select governance-form-control">
              <option v-for="option in calendar_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>

        <div v-else-if="governance_modal_kind === 'consequence'" class="row g-3">
          <div class="col-12">
            <label class="governance-field-label text-xs">Breach / incident</label>
            <input v-model="governance_modal_form.breach" type="text" class="form-control governance-form-control" placeholder="e.g. Unauthorised data sharing" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Responsible party</label>
            <input v-model="governance_modal_form.responsible_party" type="text" class="form-control governance-form-control" placeholder="Person / team" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Severity</label>
            <select v-model="governance_modal_form.severity" class="form-select governance-form-control">
              <option v-for="option in consequence_severity_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12">
            <label class="governance-field-label text-xs">Action taken</label>
            <input v-model="governance_modal_form.action_taken" type="text" class="form-control governance-form-control" placeholder="Immediate containment / response" />
          </div>
          <div class="col-12">
            <label class="governance-field-label text-xs">Corrective / disciplinary step</label>
            <input v-model="governance_modal_form.corrective_step" type="text" class="form-control governance-form-control" placeholder="e.g. Written warning + retraining" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Status</label>
            <select v-model="governance_modal_form.status" class="form-select governance-form-control">
              <option v-for="option in consequence_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>

        <div v-else class="row g-3">
          <div class="col-12">
            <label class="governance-field-label text-xs">Decision</label>
            <input v-model="governance_modal_form.decision" type="text" class="form-control governance-form-control" placeholder="e.g. All high-risk suppliers reviewed quarterly" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Decided in</label>
            <select v-model="governance_modal_form.decided_in" class="form-select governance-form-control">
              <option v-for="option in committee_type_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Approved by</label>
            <input v-model="governance_modal_form.approved_by" type="text" class="form-control governance-form-control" placeholder="e.g. Board (CEO + Chair)" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Accountable owner</label>
            <input v-model="governance_modal_form.accountable_owner" type="text" class="form-control governance-form-control" placeholder="Role - name" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Under authority</label>
            <input v-model="governance_modal_form.under_authority" type="text" class="form-control governance-form-control" placeholder="e.g. DoA-014 - above R 500k" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Authorises (linked record)</label>
            <input v-model="governance_modal_form.authorises" type="text" class="form-control governance-form-control" placeholder="e.g. Platform support agreement" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Linked risk</label>
            <input v-model="governance_modal_form.linked_risk" type="text" class="form-control governance-form-control" placeholder="e.g. R-05 - Supplier concentration" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Linked policy</label>
            <input v-model="governance_modal_form.linked_policy" type="text" class="form-control governance-form-control" placeholder="e.g. Supplier risk-rating policy" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Linked evidence</label>
            <input v-model="governance_modal_form.linked_evidence" type="text" class="form-control governance-form-control" placeholder="e.g. Quarterly review evidence folder" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Meeting date</label>
            <input v-model="governance_modal_form.meeting_date" type="text" class="form-control governance-form-control" placeholder="e.g. 12 Mar 2026 or Pending" />
          </div>
          <div class="col-12 col-md-6">
            <label class="governance-field-label text-xs">Status</label>
            <select v-model="governance_modal_form.status" class="form-select governance-form-control">
              <option v-for="option in decision_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
          <div class="col-12">
            <label class="governance-field-label text-xs">Note</label>
            <input v-model="governance_modal_form.note" type="text" class="form-control governance-form-control" placeholder="Optional note or enforcement summary" />
          </div>
        </div>
      </div>

      <div class="governance-modal-footer border-top d-flex align-items-center justify-content-end gap-2">
        <button
          v-if="governance_modal_mode === 'edit'"
          type="button"
          class="btn btn-link text-decoration-none me-auto text-danger"
          @click="delete_governance_modal"
        >
          <i class="bi bi-trash3 me-1"></i>
          <span>Delete</span>
        </button>
        <button type="button" class="btn btn-link text-decoration-none" @click="close_governance_modal">Cancel</button>
        <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2" @click="save_governance_modal">
          <i class="bi" :class="governance_modal_mode === 'edit' ? 'bi-check2-square' : 'bi-plus-lg'"></i>
          <span>{{ governance_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
        </button>
      </div>
    </section>
  </div>
</div>
